/*
 * cart-availability-guard.js
 * 数量1・重複追加防止用の軽量ガード。
 * すでにカートに入っている商品の「カートに追加」ボタンを無効化し、追加済み表示に切り替える。
 * - 商品詳細ページ: <product-form-component> 内の add-to-cart ボタン
 * - コレクション一覧の自作行カード: form.ls-lrow__cart-form の送信ボタン
 *
 * さらに、自作行カードの「購入手続きに進む」を AJAX 化し、追加後はその場で
 * 簡易完了表示（✓ カートに追加しました → カート追加済み）を出す。
 * カートドロワーは自動オープンしない（カートアイコンからのみ開く運用）。
 * テーマの 'cart:update' イベント（events.js）に連動してカート件数等を同期する。
 * モジュール依存なしのクラシックスクリプト。
 */
(function () {
  'use strict';

  var ADDED_TEXT = 'カート追加済み';
  var ADDED_FLASH_TEXT = '✓ カートに追加しました';
  var FLASH_MS = 1500;

  /**
   * カート内の variant_id 集合を取得（文字列キー）
   * @returns {Promise<Record<string, boolean>|null>}
   */
  function fetchCartVariantIds() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        /** @type {Record<string, boolean>} */
        var ids = {};
        cart.items.forEach(function (/** @type {{ variant_id: number|string }} */ item) {
          ids[String(item.variant_id)] = true;
        });
        return ids;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * ボタンの状態を切り替え（disable時は元のHTMLを退避し、復帰時に戻す）
   * @param {HTMLButtonElement|null} btn
   * @param {boolean} inCart
   * @param {boolean} replaceText
   */
  function toggleButton(btn, inCart, replaceText) {
    if (!btn) return;
    if (inCart) {
      if (btn.getAttribute('data-ls-guarded') === '1') return;
      btn.setAttribute('data-ls-guarded', '1');
      btn.setAttribute('data-ls-original-html', btn.innerHTML);
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('ls-in-cart');
      if (replaceText) btn.textContent = ADDED_TEXT;
    } else {
      if (btn.getAttribute('data-ls-guarded') !== '1') return;
      var original = btn.getAttribute('data-ls-original-html');
      if (original !== null) btn.innerHTML = original;
      btn.removeAttribute('data-ls-original-html');
      btn.removeAttribute('data-ls-guarded');
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('ls-in-cart');
    }
  }

  /**
   * 追加成功時の簡易完了表示。「✓ カートに追加しました」を一瞬出してから
   * 「カート追加済み」の無効化状態に落ち着かせる。data-ls-guarded を立てるため
   * applyState（refresh）に上書きされない。
   * @param {HTMLButtonElement|null} btn
   */
  function flashAdded(btn) {
    if (!btn) return;
    if (btn.getAttribute('data-ls-guarded') !== '1') {
      btn.setAttribute('data-ls-original-html', btn.innerHTML);
    }
    btn.setAttribute('data-ls-guarded', '1');
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('ls-added-flash');
    btn.textContent = ADDED_FLASH_TEXT;
    window.setTimeout(function () {
      btn.classList.remove('ls-added-flash');
      btn.classList.add('ls-in-cart');
      btn.textContent = ADDED_TEXT;
    }, FLASH_MS);
  }

  /**
   * @param {Record<string, boolean>|null} ids
   */
  function applyState(ids) {
    if (!ids) return;

    // 商品詳細ページ（および product-form を持つ箇所）
    document.querySelectorAll('product-form-component').forEach(function (pf) {
      var input = /** @type {HTMLInputElement|null} */ (pf.querySelector('input[name="id"]'));
      if (!input) return;
      var inCart = !!ids[String(input.value)];
      var container = pf.querySelector('add-to-cart-component');
      var btn = /** @type {HTMLButtonElement|null} */ (
        container ? container.querySelector('button[name="add"]') : null
      );
      toggleButton(btn, inCart, false);
    });

    // コレクション一覧の自作行カード
    document.querySelectorAll('form.ls-lrow__cart-form').forEach(function (/** @type {Element} */ formEl) {
      var input = /** @type {HTMLInputElement|null} */ (formEl.querySelector('input[name="id"]'));
      if (!input) return;
      var inCart = !!ids[String(input.value)];
      var btn = /** @type {HTMLButtonElement|null} */ (formEl.querySelector('button[type="submit"]'));
      toggleButton(btn, inCart, true);
    });
  }

  function refresh() {
    fetchCartVariantIds().then(applyState);
  }

  /**
   * 行カード(.ls-lrow)を含む <product-card> のカード全体ナビゲーションを無効化する。
   * product-card.js は data-no-navigation 属性があると navigateToProduct で遷移しない。
   * これで「詳細を見る」ボタン(<a>)以外のクリックでは商品ページへ遷移しなくなる。
   */
  function disableRowCardNavigation() {
    document.querySelectorAll('.ls-lrow').forEach(function (row) {
      var card = row.closest('product-card');
      if (card && !card.hasAttribute('data-no-navigation')) {
        card.setAttribute('data-no-navigation', '');
      }
    });
  }

  /** フィルタ・並び替えでグリッドが再描画されても再適用する */
  function observeRowCards() {
    if (typeof MutationObserver === 'undefined') return;
    var lists = document.querySelectorAll('results-list');
    if (!lists.length) return;
    var observer = new MutationObserver(function () {
      disableRowCardNavigation();
    });
    lists.forEach(function (list) {
      observer.observe(list, { childList: true, subtree: true });
    });
  }

  /** @returns {string} カート追加URL（ロケール対応） */
  function cartAddUrl() {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) {
      return Theme.routes.cart_add_url;
    }
    return '/cart/add';
  }

  /**
   * コレクション一覧の自作ボタンを AJAX 化。
   * 追加後に 'cart:update' を発火してカート件数・ドロワー内容を同期し、
   * ボタンには簡易完了表示を出す（ドロワーは自動で開かない）。
   * JSが無効な場合は通常のフォーム送信（return_to でカートへ遷移）にフォールバック。
   * @param {HTMLFormElement} form
   */
  function handleListRowSubmit(form) {
    var btn = /** @type {HTMLButtonElement|null} */ (form.querySelector('button[type="submit"]'));
    if (btn && btn.disabled) return;
    if (btn) btn.disabled = true;

    var formData = new FormData(form);
    formData.delete('return_to'); // AJAXではリダイレクト不要

    // 商品詳細ページと同じく、追加リクエストでカート関連セクションのHTMLも取得し、
    // 1往復で完結させる（イベント後の再フェッチを避けてラグをなくす）。
    /** @type {string[]} */
    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId && sectionIds.indexOf(el.dataset.sectionId) === -1) {
        sectionIds.push(el.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then(function (res) {
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (data) {
        // エラー（在庫切れ等）はボタンを戻して終了
        if (data && data.status) {
          if (btn) btn.disabled = false;
          return;
        }
        // テーマのカート更新フローに乗せる（カート件数・ドロワー内容の同期）。
        // 自動オープン設定がオフなら cart-drawer はこのイベントでは開かない。
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: 'ls-list-row',
              data: { source: 'ls-list-row', itemCount: 1, sections: data ? data.sections : undefined },
            },
          })
        );
        // 簡易完了表示（✓ カートに追加しました → カート追加済み）
        if (btn) flashAdded(btn);
      })
      .catch(function () {
        if (btn) btn.disabled = false;
      });
  }

  document.addEventListener('submit', function (event) {
    var form = /** @type {HTMLFormElement} */ (event.target);
    if (!form || !form.classList || !form.classList.contains('ls-lrow__cart-form')) return;
    event.preventDefault();
    handleListRowSubmit(form);
  });

  // カート更新（追加・数量変更・削除）に連動
  document.addEventListener('cart:update', refresh);

  function init() {
    refresh();
    disableRowCardNavigation();
    observeRowCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
